import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Category,
  CategoryFilters,
  MetadataStatus,
  Locale,
} from "../../model/Category";
import CategoryFiltersEditor from "./CategoryFiltersEditor";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import { reportError } from "../../helpers/reportError";

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

function parseFilters(raw?: string | null): CategoryFilters | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isObject(parsed)) return null;

    const search = isObject(parsed.search) ? parsed.search : {};
    const lockedFacets = Array.isArray(parsed.lockedFacets)
      ? parsed.lockedFacets.filter(
          (facet): facet is string => typeof facet === "string",
        )
      : [];

    return { search, lockedFacets } as CategoryFilters;
  } catch {
    return null;
  }
}

function slugify(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

async function revalidateStorefront(): Promise<void> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 2000);

  try {
    const response = await fetch("/api/internal/revalidate-storefront", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ tags: ["categories"] }),
      signal: controller.signal,
    });
    if (!response.ok) {
      reportError(
        "categories.revalidate",
        new Error(`HTTP ${response.status}`),
        {
          "http.status": response.status,
        },
      );
    }
  } catch (error) {
    reportError("categories.revalidate", error);
  } finally {
    clearTimeout(timeout);
  }
}

interface ManageCategoryProps {
  category?: Category;
  onCategoryUpdate: (category: Category) => void;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
  categories: Category[]; // For parent selection
}

const ManageCategory: React.FC<ManageCategoryProps> = ({
  category,
  onCategoryUpdate,
  setError,
  setSuccess,
  categories,
}) => {
  const [formData, setFormData] = useState<Category>(() => ({
    slug: category?.slug ?? "",
    name: category?.name ?? "",
    order: category?.order ?? 0,
    image: category?.image ?? "",
    icon: category?.icon ?? "",
    showcase: category?.showcase ?? true,
    searchable: category?.searchable ?? true,
    filterable: category?.filterable ?? true,
    minisite: category?.minisite ?? false,
    navigation: category?.navigation ?? false,
    status: category?.status ?? MetadataStatus.PUBLISHED,
    parentId: category?.parentId,
    display_name: { [Locale.PT]: "", ...(category?.display_name ?? {}) },
    description: { [Locale.PT]: "", ...(category?.description ?? {}) },
    filters: category?.filters ?? null,
  }));

  const [loading, setLoading] = useState(false);
  const submittingRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      // Cancels the request if the modal closes/switches before it settles.
      abortRef.current?.abort();
    },
    [],
  );
  const [filters, setFilters] = useState<CategoryFilters | null>(() =>
    parseFilters(category?.filters),
  );
  // Existing categories keep their published slug unless the admin edits the
  // slug field directly. New categories follow the name until the slug is touched.
  const [slugTouched, setSlugTouched] = useState(!!category?.slug);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      if (name === "slug") setSlugTouched(true);
      setFormData((prev) => {
        const next = { ...prev, [name]: value };
        if (name === "name" && !slugTouched) {
          next.slug = slugify(value);
        }
        return next;
      });
    }
  };

  const handleLocaleChange = (
    field: "display_name" | "description",
    locale: string,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [locale]: value,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;

    setLoading(true);
    setError(null);
    setSuccess(null);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // Explicit whitelist: never echo `id`/`children` or unvalidated fields
      // from the server payload back to the API.
      const payload = {
        slug: formData.slug,
        name: formData.name,
        order: Number(formData.order) || 0,
        image: formData.image ?? null,
        icon: formData.icon ?? null,
        showcase: formData.showcase,
        searchable: formData.searchable,
        filterable: formData.filterable,
        minisite: formData.minisite,
        navigation: formData.navigation,
        status: formData.status,
        parentId: formData.parentId ?? null,
        display_name: formData.display_name ?? {},
        description: formData.description ?? {},
        filters: filters ? JSON.stringify(filters) : null,
      };
      const config = { timeout: 20000, signal: controller.signal };

      let response;
      if (category?.id != null) {
        response = await api.put(
          `${API_URLS.CATEGORIES}/${category.id}`,
          payload,
          config,
        );
      } else {
        response = await api.post(API_URLS.CATEGORIES, payload, config);
      }
      // Best-effort: purge the storefront's cached category tree so the recipe
      // (noindex/resolution) applies immediately instead of up to 1h later.
      // It must never block the save or the modal close.
      onCategoryUpdate(response.data);
      void revalidateStorefront();
    } catch (error: any) {
      const canceled =
        error?.code === "ERR_CANCELED" ||
        error?.name === "CanceledError" ||
        error?.name === "AbortError";
      if (canceled) return;

      const status = error.response?.status;
      const backendMessage = error.response?.data?.message;
      const message =
        status === 409
          ? "Já existe uma categoria com este slug."
          : backendMessage ||
            (error.request
              ? "Não foi possível contactar o servidor."
              : error.message) ||
            "Erro desconhecido";

      reportError("categories.save", error, {
        "http.status": status,
        "category.id": category?.id,
      });
      setError(`Erro ao guardar categoria: ${message}`);
    } finally {
      submittingRef.current = false;
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      setLoading(false);
    }
  };

  const parentOptions = useMemo(() => {
    const result: { id: number; name: string }[] = [];

    const walk = (nodes: Category[], prefix: string) => {
      for (const node of nodes) {
        // Skip the edited node and its whole subtree (prevents cycles).
        if (node.id === category?.id) continue;
        if (node.id != null) {
          result.push({ id: node.id, name: prefix + node.name });
        }
        if (node.children?.length) {
          walk(node.children, `${prefix}-- `);
        }
      }
    };

    walk(categories, "");
    return result;
  }, [categories, category?.id]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nome (Interno) *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            maxLength={120}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Nome interno da categoria"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Slug *
          </label>
          <input
            type="text"
            name="slug"
            value={formData.slug}
            onChange={handleChange}
            required
            maxLength={160}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="slug-da-categoria"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ordem
          </label>
          <input
            type="number"
            name="order"
            value={formData.order}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {Object.values(MetadataStatus).map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Categoria Pai
          </label>
          <select
            name="parentId"
            value={formData.parentId || ""}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                parentId: e.target.value ? Number(e.target.value) : undefined,
              }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Nenhuma (Raiz)</option>
            {parentOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="showcase"
            checked={formData.showcase}
            onChange={handleChange}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">Showcase</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="searchable"
            checked={formData.searchable}
            onChange={handleChange}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">Pesquisável</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="filterable"
            checked={formData.filterable}
            onChange={handleChange}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">Filtrável</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="minisite"
            checked={formData.minisite}
            onChange={handleChange}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">Minisite</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="navigation"
            checked={formData.navigation}
            onChange={handleChange}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">Navegação</span>
        </label>
      </div>

      <CategoryFiltersEditor value={filters} onChange={setFilters} />

      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Traduções</h3>
        <div className="space-y-6">
          {Object.values(Locale).map((locale) => (
            <div
              key={locale}
              className="bg-gray-50 p-4 rounded-lg border border-gray-200"
            >
              <h4 className="font-medium text-sm text-gray-900 mb-3 flex items-center gap-2">
                <span className="uppercase bg-white px-2 py-0.5 rounded border text-xs">
                  {locale}
                </span>
              </h4>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Nome de Exibição
                  </label>
                  <input
                    type="text"
                    value={formData.display_name[locale] || ""}
                    onChange={(e) =>
                      handleLocaleChange("display_name", locale, e.target.value)
                    }
                    maxLength={120}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder={`Nome em ${locale}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Descrição
                  </label>
                  <textarea
                    value={formData.description[locale] || ""}
                    onChange={(e) =>
                      handleLocaleChange("description", locale, e.target.value)
                    }
                    rows={2}
                    maxLength={2000}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder={`Descrição em ${locale}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t">
        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition font-medium"
        >
          {loading
            ? "A guardar..."
            : category?.id
              ? "Atualizar Categoria"
              : "Criar Categoria"}
        </button>
      </div>
    </form>
  );
};

export default ManageCategory;
